export class VideoPlayerButton extends HTMLButtonElement {
  constructor () {
    super()
    this.timeout = null // Store timeout for hiding the button
    this.selectors = {
      video: ''
    }
    this.init()
  }

  /**
   * inits the video selector
   */
  init () {
    this.nativeVideo = true
    this.selectors.video = 'video'
  }

  /**
   * Default web component method
   */
  connectedCallback () {
    this.video = this.parentElement.querySelector(this.selectors.video)
    // Ensure required elements exist
    if (!this.video) {
      console.error('Video element not found.')
      return
    }
    this.buttonMountedCallback()
  }

  /**
   * method called in connectedCallback method
   */
  buttonMountedCallback () {
    // Initialize button state
    this.updateButtonState()
    // Add event listeners
    this.addEventListeners()
  }

  /**
   * Adds event listeners to the play/pause button on connectedCallback
   */
  addEventListeners () {
    this.addEventListener('click', this.handleClickState.bind(this))
    this.addEventListener('mouseon', this.handleMouseOn.bind(this))
    this.nextElementSibling.addEventListener('mousemove', this.handleMouseMove.bind(this))
    this.video.addEventListener('mouseout', this.handleMouseOut.bind(this))
    this.nextElementSibling.addEventListener('touchstart', this.handleMouseMove.bind(this))
    if (this.nativeVideo) {
      this.video.addEventListener('play', this.handleVideoPlayPause.bind(this))
      this.video.addEventListener('pause', this.handleVideoPlayPause.bind(this))
      this.video.addEventListener('ended', this.handleVideoEnd.bind(this))
    }
  }

  /**
   * Removes event listeners to the play/pause button on connectedCallback
   */
  removeEventListeners () {
    this.removeEventListener('click', this.handleClickState.bind(this))
    this.removeEventListener('mouseon', this.handleMouseOn.bind(this))
    this.video.removeEventListener('mousemove', this.handleMouseMove.bind(this))
    this.video.addEventListener('mouseout', this.handleMouseOut.bind(this))
    this.nextElementSibling.removeEventListener('touchstart', this.handleMouseMove.bind(this))
    if (this.nativeVideo) {
      this.video.removeEventListener('play', this.handleVideoPlayPause.bind(this))
      this.video.removeEventListener('pause', this.handleVideoPlayPause.bind(this))
      this.video.removeEventListener('ended', this.handleVideoEnd.bind(this))
    }
  }

  /**
   * Handles play and pause state,
   * will be trigered by play and pause events
   */
  handleVideoPlayPause () {
    this.updateButtonState()
    this.disappearButton()
  }

  /**
   * Ensure the button is visible when the video ends
   */
  handleVideoEnd () {
    this.showButton()
    this.updateButtonState(true) // Reset to play state
  }

  /**
   * Ensure the button is visible when mousemove on video
   */
  handleMouseMove () {
    this.disappearButton()
    this.updateButtonState()
  }

  /**
   * Ensure the button is visible when hovers on video
   */
  handleMouseOn () {
    this.showButton()
  }

  /**
   * Ensure the button is hidden when mouseout on video
   */
  handleMouseOut () {
    this.disappearButton()
    this.updateButtonState()
  }

  /**
   * get video duration
   * @returns {Number}
   */
  getVideoDuration () {
    return this.video.duration
  }

  /**
   * get the button state
   * @returns {String}
   */
  getVideoState () {
    let status = 'playing'
    if (this.video.paused) status = 'paused'
    return status
  }

  /**
   * Update the button body and attributes depends on state
   * @param {Boolean} resetToPlay=false
   * @returns {undefined}
   */
  updateButtonState (resetToPlay = false) {
    const videoDuration = this.getVideoDuration()
    if (videoDuration < 5) {
      this.removeThis()
      return
    }

    const videoStatus = this.getVideoState()
    if (resetToPlay || videoStatus === 'paused') {
      // Update button to show play icon
      this.setAttribute('aria-label', window.videoButtonStates.playButton)
      this.setAttribute('aria-pressed', 'false')
      this.setAttribute('data-state', 'play')
    } else if (videoStatus === 'playing') {
      // Update button to show pause icon
      this.setAttribute('aria-label', window.videoButtonStates.pauseButton)
      this.setAttribute('aria-pressed', 'true')
      this.setAttribute('data-state', 'pause')
    }
  }

  /**
   * Shows button on pause state
   * @returns {any}
   */
  showButton () {
    this.classList.remove('sr-only')
    this.setAttribute('aria-expanded', 'true')
    clearTimeout(this.timeout)
  }

  /**
   * Always show the button on interaction
   * and hides button after 2.5 second of inactivity
   */
  disappearButton () {
    this.classList.remove('sr-only')
    this.setAttribute('aria-expanded', 'true')

    // Clear existing timeout and set a new one
    clearTimeout(this.timeout)
    const videoStatus = this.getVideoState()
    if (videoStatus !== 'paused') {
      this.timeout = setTimeout(() => {
        this.classList.add('sr-only')
        this.setAttribute('aria-expanded', 'false')
      }, 2500)
    }
  }

  /**
   * Method to Play the video
   */
  playVideo () {
    this.video.play()
  }

  /**
   * Method to pause the video
   */
  pauseVideo () {
    this.video.pause()
  }

  /**
   * Handles pause and play state of the button
   * @returns {any}
   */
  handleClickState () {
    const videoStatus = this.getVideoState()
    if (videoStatus === 'paused' || videoStatus === 'videoCued') {
      this.playVideo()
    } else {
      this.pauseVideo()
    }
  }

  /**
   * Default method of web components
   * @returns {any}
   */
  disconnectedCallback () {
    this.removeEventListeners()
  }

  /**
   * returns attributes to observe
   * @returns {Array[string]}
   */
  static get observedAttributes () {
    return ['data-play']
  }

  /**
   * callback called on attribute change
   * @param {string} name
   * @param {any} oldValue
   * @param {any} newValue
   */
  attributeChangedCallback (name, oldValue, newValue) {
    if (name === 'data-play' && newValue === 'true') {
      this.playVideo()
    }
  }

  /**
   * Removes the button if video is less than 5sec
   */
  removeThis () {
    this.remove()
  }
}

// Define the custom button element
customElements.define('video-player-button', VideoPlayerButton, { extends: 'button' })

export default VideoPlayerButton
